import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ChatMessages from './ChatMessages/ChatMessages';
import ChatInput from './ChatInput';
import './ChatWindow.css';

const ChatWindow = ({
    chat,
    currentUserId,
    messages = [],
    typingUsers = [],
    newMessage = '',
    setNewMessage,
    handleTyping,
    onSendMessage,
    onReactToMessage,
    onLoadMore,
    hasMore = false,
    markMessagesSeen
}) => {
    const chatContainerRef = useRef(null);
    const chatEndRef = useRef(null);
    const unreadRef = useRef(null);

    // --- State & Refs for Pagination/Scrolling ---
    const [replyTo, setReplyTo] = useState(null);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const prevScrollHeightRef = useRef(0);
    const prevMessageCount = useRef(0);
    const initialScrollDone = useRef(false); // ✅ Added missing ref

    const chatId = chat?._id || chat?.id || null;
    const resolvedUserId = String(currentUserId || '');

    // ---------------------------------------------
    // 📜 1. Advanced Scrolling Logic
    // ---------------------------------------------

    const scrollToBottom = useCallback((smooth = true) => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'end',
            });
        }
    }, []);

    // ✅ Reset initial scroll when switching chats
    useEffect(() => {
        initialScrollDone.current = false;
    }, [chatId]);

    // ✅ THE MASTER SCROLL LOGIC
    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container || messages.length === 0) return;

        // --- 1. INITIAL LOAD (Jump to Unread or Bottom) ---
        if (!initialScrollDone.current) {
            if (unreadRef.current) {
                unreadRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
            } else {
                scrollToBottom(false); // ✅ Instantly jumps to the bottom if no unread!
            }

            initialScrollDone.current = true;
            prevScrollHeightRef.current = container.scrollHeight;
            prevMessageCount.current = messages.length;
            return; // Exit early so pagination logic doesn't run
        }

        // --- 2. PAGINATION (Loading older messages) ---
        if (prevScrollHeightRef.current > 0 && messages.length > prevMessageCount.current) {
            const isAtTop = container.scrollTop < 100;
            if (isAtTop) {
                const heightDifference = container.scrollHeight - prevScrollHeightRef.current;
                container.scrollTop = container.scrollTop + heightDifference;
            }
        }

        // --- 3. NEW INCOMING MESSAGES ---
        const isNewMessage = messages.length > prevMessageCount.current;
        const isNearBottom = (container.scrollHeight - container.scrollTop - container.clientHeight) < 150;

        if (isNewMessage && isNearBottom) {
            scrollToBottom(true);
        }

        // Update tracking refs
        prevScrollHeightRef.current = container.scrollHeight;
        prevMessageCount.current = messages.length;
    }, [messages, chatId, scrollToBottom]);

    // ✅ TRIGGER PAGINATION: Triggered when scrolling near the top
    const handleScroll = useCallback(async () => {
        const container = chatContainerRef.current;
        if (!container || !hasMore || !onLoadMore || isLoadingMore) return;

        if (container.scrollTop < 50) {
            setIsLoadingMore(true);

            // Capture height before loading
            prevScrollHeightRef.current = container.scrollHeight;

            await onLoadMore(chatId);
            setIsLoadingMore(false);
        }
    }, [hasMore, onLoadMore, chatId, isLoadingMore]);

    // ---------------------------------------------
    // 👁️ 2. Read Receipts
    // ---------------------------------------------
    useEffect(() => {
        if (chatId && resolvedUserId && markMessagesSeen) {
            markMessagesSeen(chatId, resolvedUserId);
        }
    }, [chatId, messages.length, resolvedUserId, markMessagesSeen]);

    // ---------------------------------------------
    // ✉️ 3. Message Handling
    // ---------------------------------------------
    const handleSend = (text) => {
        if (typeof text !== 'string' || !text.trim()) return;
        onSendMessage({
            chatId,
            content: text.trim(),
            replyTo: replyTo?._id || replyTo?.id || null
        });
        setReplyTo(null);
    };

    const handleJumpToMessage = useCallback((messageId) => {
        setHighlightedMessageId(messageId);
        const element = document.getElementById(`message-${messageId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setTimeout(() => setHighlightedMessageId(null), 2000);
    }, []);

    if (!chatId) return <div className="chat-window empty">⚠️ No active chat selected.</div>;

    return (
        <div className="chat-window">
            <div
                className="chat-messages-container"
                ref={chatContainerRef}
                onScroll={handleScroll}
            >
                {/* 🔃 Pagination Indicator */}
                {hasMore && (
                    <div className="load-more-indicator">
                        {isLoadingMore ? "Fetching history..." : "Scroll up to load older messages"}
                    </div>
                )}

                <ChatMessages
                    messages={messages}
                    typingUsers={typingUsers}
                    unreadRef={unreadRef}
                    chatEndRef={chatEndRef}
                    onReply={setReplyTo}
                    currentUserId={resolvedUserId}
                    onReact={(messageId, emoji) => onReactToMessage?.(chatId, messageId, emoji)}
                    highlightedMessageId={highlightedMessageId}
                    onJumpToMessage={handleJumpToMessage}
                />

                <div ref={chatEndRef} className="chat-end-anchor" style={{ height: '1px' }} />
            </div>

            <ChatInput
                value={newMessage}
                onChange={setNewMessage}
                onSend={handleSend}
                onTypingStart={() => handleTyping?.(chatId, true)}
                onTypingStop={() => handleTyping?.(chatId, false)}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
            />
        </div>
    );
};

ChatWindow.propTypes = {
    chat: PropTypes.object.isRequired,
    currentUserId: PropTypes.string.isRequired,
    messages: PropTypes.array,
    typingUsers: PropTypes.array,
    newMessage: PropTypes.string,
    setNewMessage: PropTypes.func.isRequired,
    handleTyping: PropTypes.func,
    onSendMessage: PropTypes.func.isRequired,
    onReactToMessage: PropTypes.func,
    onLoadMore: PropTypes.func,
    hasMore: PropTypes.bool,
    markMessagesSeen: PropTypes.func
};

export default ChatWindow;